import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, euro, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Printer, MoreHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  borrador: "bg-[#EAE6DF] text-[#2A2F2C]",
  aceptado: "bg-[#D1E0D7] text-[#2A2F2C]",
  rechazado: "bg-[#E8D1D1] text-[#2A2F2C]",
  convertido: "bg-[#F3E5D8] text-[#2A2F2C]",
};

export default function Quotes() {
  const { user, centers } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [centerFilter, setCenterFilter] = useState("all");

  const load = async () => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    const [q, p] = await Promise.all([
      api.get("/quotes", { params }).then((r) => r.data),
      api.get("/patients", { params }).then((r) => r.data),
    ]);
    setQuotes(q);
    setPatients(p);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [centerFilter]);

  const openPdf = async (q) => {
    try {
      const resp = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (q, status) => {
    try {
      await api.patch(`/quotes/${q.id}/status?status=${status}`);
      toast.success("Estado actualizado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const convert = async (q) => {
    if (!window.confirm(`¿Convertir presupuesto ${q.quote_number} en factura?`)) return;
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert`);
      toast.success(`Factura ${data.invoice_number} creada`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (q) => {
    if (!window.confirm("¿Eliminar presupuesto?")) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const patientName = (id) => {
    const p = patients.find((x) => x.id === id);
    return p ? `${p.name} ${p.surname || ""}`.trim() : "—";
  };
  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Comercial
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Presupuestos
          </h1>
          <p className="text-sm text-[#6E7772] mt-1">
            Para emitir un nuevo presupuesto, accede a la ficha del paciente.
          </p>
        </div>
        {["admin", "center_admin"].includes(user?.role) && (
          <div className="w-56">
            <Select value={centerFilter} onValueChange={setCenterFilter}>
              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E5E3DB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E3DB]">
              <TableHead>Nº</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Válido hasta</TableHead>
              <TableHead>Paciente</TableHead>
              <TableHead>Centro</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[#6E7772] py-12">
                  <FileText strokeWidth={1.5} className="h-8 w-8 mx-auto mb-2 opacity-60" />
                  Sin presupuestos.
                </TableCell>
              </TableRow>
            )}
            {quotes.map((q) => (
              <TableRow key={q.id} className="border-[#E5E3DB]" data-testid={`quote-row-${q.id}`}>
                <TableCell className="font-mono text-sm">{q.quote_number}</TableCell>
                <TableCell>{formatDate(q.date)}</TableCell>
                <TableCell className="text-[#6E7772]">{formatDate(q.valid_until)}</TableCell>
                <TableCell>
                  <Link to={`/pacientes/${q.patient_id}`} className="text-[#4A5D4E] hover:underline">
                    {patientName(q.patient_id)}
                  </Link>
                </TableCell>
                <TableCell>{centerName(q.center_id)}</TableCell>
                <TableCell className="text-right font-medium">{euro(q.total)}</TableCell>
                <TableCell>
                  <Badge className={STATUS_COLOR[q.status] || "bg-[#EAE6DF]"}>
                    {q.status}
                  </Badge>
                </TableCell>
                <TableCell className="space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openPdf(q)}
                    data-testid={`quote-pdf-${q.id}`}
                  >
                    <Printer className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  <Link
                    to={`/pacientes/${q.patient_id}`}
                    className="inline-block text-xs text-[#4A5D4E] hover:underline ml-2"
                    data-testid={`quote-goto-patient-${q.id}`}
                    title="Para cambiar el estado, accede a la ficha del paciente"
                  >
                    Ficha paciente
                  </Link>
                  {["admin", "center_admin"].includes(user?.role) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {q.status !== "convertido" && (
                          <DropdownMenuItem onClick={() => convert(q)}>
                            Convertir en factura
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => remove(q)} className="text-[#C07F67]">
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
